// src/components/Item.jsx - ACTUALIZADO con getDaysText corregido
import React, { useState } from 'react';
import { Menu } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useItemFilters } from '../hooks/useItemFilters';
import QuickEditModal from './QuickEditModal';

const Item = ({ 
  item, 
  isInList, 
  showDaysSince = false, 
  onCheckToggle, 
  onDelete, 
  onEdit, 
  onQuantityChange,
  onAddToList,
  onRemoveFromList,
  onChangeList,
  showToast,
  onUpdateItem,
  availableCategories = [],
  currentView = 'list', // 'list', 'catalog', o 'search'
  activeList
}) => {
  const { t } = useTranslation();
  const { getDaysSinceLastUsed } = useItemFilters();
  
  const daysSince = showDaysSince ? getDaysSinceLastUsed(item.date) : null;
  const [isChecked, setIsChecked] = useState(parseInt(item.status) === 1);
  const isSavedForLater = parseInt(item.later) === 1;
  const shouldStrikeThrough = isInList && !isSavedForLater && isChecked;
  const price = parseFloat(item.price) || 0;
  const formattedPrice = price === 0 ? '0.00' : price.toFixed(2);
  const hasNote = item.note && item.note.trim() !== '';

  const [quickEditModal, setQuickEditModal] = useState({
    isOpen: false,
    field: null,
    currentValue: '',
    availableCategories: []
  });

  React.useEffect(() => {
    setIsChecked(parseInt(item.status) === 1);
  }, [item.status]);

  const getDaysText = (days) => {
    if (days === null || days === undefined) return '';
    if (days === 0) return t('item.today');
    if (days === 1) return t('item.yesterday');
    return t('item.daysAgo', { count: days });
  };

  const handleCheckToggle = (e) => {
    e.stopPropagation();
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    onCheckToggle(item.id);
  };

  const openQuickEdit = (field) => {
    let currentValue = '';
    switch (field) {
      case 'rename':
        currentValue = item.item;
        break;
      case 'price':
        currentValue = price > 0 ? price.toString() : '';
        break;
      case 'note':
        currentValue = item.note || '';
        break;
      case 'category':
        currentValue = item.category || 'others';
        break;
    }
    
    setQuickEditModal({
      isOpen: true,
      field,
      currentValue,
      availableCategories
    });
  };

  const closeQuickEdit = () => {
    setQuickEditModal({ isOpen: false, field: null, currentValue: '', availableCategories: [] });
  };

  const handleQuickEditSave = async (updateData) => {
    try {
      if (onUpdateItem) {
        await onUpdateItem(item.id, updateData);
      }
    } catch (error) {
      console.error('Error saving quick edit:', error);
    }
  };

  const formatCategory = (category) => {
    if (!category) return '';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  const renderQuantitySection = () => {
    if (!isInList) return null;

    const quantity = item.quantity || 1;
    const totalPrice = (price * quantity).toFixed(2);
    const listName = item.list_name || activeList?.name || t('common.list');

    return (
      <div className="flex flex-col items-end mr-2">
        <div className="mb-1">
          <div className="bg-purple-600 rounded-full flex items-center overflow-hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (quantity > 1) onQuantityChange(item.id, quantity - 1);
              }}
              className={`px-3 py-[2px] text-white transition-colors cursor-pointer ${quantity > 1 ? 'hover:bg-purple-700' : 'opacity-50 cursor-not-allowed'}`}
              disabled={quantity <= 1}
              aria-label={t('item.decreaseQty')}
            >
              <i className="fas fa-minus text-xs"></i>
            </button>
            
            <span className="px-3 py-[2px] text-white font-medium min-w-[20px] text-center cursor-default">
              {quantity}
            </span>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(item.id, quantity + 1);
              }}
              className="px-3 py-[2px] text-white transition-colors hover:bg-purple-700 cursor-pointer"
              aria-label={t('item.increaseQty')}
            >
              <i className="fas fa-plus text-xs"></i>
            </button>
          </div>
        </div>
        
        {currentView === 'list' ? (
          <div className="text-xs text-gray-600">
            <span className="font-medium">{t('item.total')}</span>
            <span className="ml-1 font-semibold">${totalPrice}</span>
          </div>
        ) : (
          <div className="text-xs text-gray-500 flex items-center">
            {t('item.in')} <span className="font-medium text-purple-600 ml-1">{listName}</span>
            <span className="mx-1">|</span>
            <span className="font-semibold">{t('item.total')} ${totalPrice}</span>
          </div>
        )}
      </div>
    );
  };

  const renderAddToListSection = () => {
    if (isInList) return null;

    return (
      <div className="flex flex-col items-end mr-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddToList(item.id);
          }}
          className="px-4 py-1 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-medium text-sm whitespace-nowrap flex items-center mb-1 cursor-pointer"
        >
          <i className="fas fa-cart-plus mr-1 text-xs"></i>
          {t('item.addToList')}
        </button>
        
        {showDaysSince && daysSince !== null && daysSince >= 0 && (
          <div className="text-xs text-gray-500 cursor-default">
            {t('item.purchased', { time: getDaysText(daysSince) })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between p-2 pb-1 border-t border-gray-200 bg-white">
        <div className="flex items-center flex-1 min-w-0 cursor-default">
          {isInList && !isSavedForLater && (
            <div className="relative mr-2">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckToggle}
                className="mt-0 h-7 w-7 appearance-none rounded border-2 border-gray-300 checked:border-purple-600 checked:bg-purple-600 cursor-pointer"
              />
              {isChecked && (
                <i className="fas fa-check absolute left-1/2 top-[15px] transform -translate-x-1/2 -translate-y-1/2 text-white text-sm pointer-events-none"></i>
              )}
            </div>
          )}
          
          <div className="min-w-0">
            <div className="flex items-center">
              <p className={`truncate ${shouldStrikeThrough ? 'line-through text-gray-500' : 'text-gray-900'} capitalize`}>
                {item.item}
              </p>
              {hasNote && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickEdit('note');
                  }}
                  className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-yellow-50 ml-0 transition-colors cursor-pointer"
                  title={t('item.editNoteTitle', { note: item.note })}
                >
                  <i className="fas fa-file-alt text-yellow-500 text-sm"></i>
                </button>
              )}
            </div>
            
            <div className="flex items-center">
              <p className="text-sm font-bold text-gray-700">
                ${formattedPrice}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center">
          {isInList ? renderQuantitySection() : renderAddToListSection()}
          
          <Menu as="div" className="relative">
            <Menu.Button 
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="fas fa-ellipsis-v text-gray-500"></i>
            </Menu.Button>
            
            <Menu.Items anchor="bottom end" className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 w-52">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 inline-block min-w-[140px]">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuickEdit('rename');
                      }}
                    >
                      <i className="fas fa-edit mr-2 text-gray-500"></i>
                      {t('item.rename')}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuickEdit('price');
                      }}
                    >
                      <i className="fas fa-dollar-sign mr-2 text-gray-500"></i>
                      {t('item.changePrice')}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuickEdit('category');
                      }}
                    >
                      <i className="fas fa-tag mr-2 text-gray-500"></i>
                      {t('item.changeCategory')}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuickEdit('note');
                      }}
                    >
                      <i className="fas fa-sticky-note mr-2 text-gray-500"></i>
                      {hasNote ? t('item.editNote') : t('item.addNote')}
                    </button>
                  )}
                </Menu.Item>
                
                {isInList && onChangeList && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeList(item);
                        }}
                      >
                        <i className="fas fa-exchange-alt mr-2 text-gray-500"></i>
                        {t('item.changeList')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                
                {isInList ? (
                  <>
                    {currentView === 'list' && onUpdateItem && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onUpdateItem(item.id, { later: isSavedForLater ? 0 : 1 });
                            }}
                          >
                            <i className={`fas ${isSavedForLater ? 'fa-undo' : 'fa-calendar-alt'} mr-2 text-gray-500`}></i>
                            {isSavedForLater ? t('item.moveBackToList') : t('item.saveForLater')}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <i className="fas fa-info-circle mr-2 text-gray-500"></i>
                          {t('item.viewDetails')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveFromList(item.id);
                          }}
                        >
                          <i className="fas fa-minus-circle mr-2 text-gray-500"></i>
                          {t('item.removeFromList')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item.id);
                          }}
                        >
                          <i className="fas fa-trash-alt mr-2 text-gray-500"></i>
                          {t('item.delete')}
                        </button>
                      )}
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <i className="fas fa-info-circle mr-2 text-gray-500"></i>
                          {t('item.viewDetails')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap ${active ? 'bg-gray-50' : 'text-gray-700'} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item.id);
                          }}
                        >
                          <i className="fas fa-trash-alt mr-2 text-gray-500"></i>
                          {t('item.delete')}
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </div>

      {quickEditModal.isOpen && (
        <QuickEditModal
          isOpen={quickEditModal.isOpen}
          onClose={closeQuickEdit}
          field={quickEditModal.field}
          currentValue={quickEditModal.currentValue}
          itemId={item.id}
          itemName={item.item}
          onSaveSuccess={handleQuickEditSave}
          showToast={showToast}
          availableCategories={quickEditModal.availableCategories}
        />
      )}
    </>
  );
};

export default Item;